import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
	providedIn: 'root'
})
export class GushParcelSearchService {

	constructor(private translate: TranslateService) {}

	detectGushParcelFields(fields: any[]): { gushFieldName: string; parcelFieldName: string } {
		const gushFields = fields.filter((field: any) => field.name.toUpperCase().includes('GUSH'));
		const parcelFields = fields.filter((field: any) => {
			const name = field.name.toUpperCase();
			return name.includes('PARCEL') || name.includes('HELKA') || name.includes('CHELKA');
		});
		
		const gushFieldName = gushFields.find((f: any) => f.name.toUpperCase() === 'GUSH_NUM')?.name || 
							  gushFields.find((f: any) => !f.name.toUpperCase().includes('_ID'))?.name || 
							  gushFields[0]?.name || 'GUSH_NUM';
		
		const parcelFieldName = parcelFields.find((f: any) => f.name.toUpperCase() === 'PARCEL')?.name || 
								parcelFields.find((f: any) => !f.name.toUpperCase().includes('_ID'))?.name || 
								parcelFields[0]?.name || 'PARCEL_NUM';
		
		return { gushFieldName, parcelFieldName };
	}

	getSuggestions(params: any, layer: any, gushFieldName: string, parcelFieldName: string): Promise<any[]> {
		const parts = params.suggestTerm.split(/[\s\/\-,]+/).filter((p: string) => p.trim() && !isNaN(Number(p)));
		
		if (parts.length === 0) {
			return Promise.resolve([]);
		}
		
		const [gushStr, parcelStr] = parts;
		const gush = parseInt(gushStr, 10);
		
		if (isNaN(gush)) {
			return Promise.resolve([]);
		}
		
		let where: string;
		if (parts.length === 1) {
			// Just gush - show all parcels in this gush
			where = `${gushFieldName} = ${gush}`;
		} else {
			// Gush and parcel (partial or complete)
			const parcel = parseInt(parcelStr, 10);
			if (isNaN(parcel)) {
				where = `${gushFieldName} = ${gush}`;
			} else {
				const parcelLength = parcelStr.length;
				
				// If parcel is 1 or 2 digits, treat as exact match or starts-with
				if (parcelLength <= 2) {
					// For single digit like "1", show both exact match (1) and range (10-19)
					// For two digits like "12", show exact match (12) and range (120-129)
					const exactMatch = parcel;
					const rangeStart = parcel * 10;
					const rangeEnd = (parcel + 1) * 10 - 1;
					where = `${gushFieldName} = ${gush} AND (${parcelFieldName} = ${exactMatch} OR (${parcelFieldName} >= ${rangeStart} AND ${parcelFieldName} <= ${rangeEnd}))`;
				} else {
					// For 3+ digits, just use exact match and starts-with range
					const rangeStart = parcel * Math.pow(10, Math.max(0, 4 - parcelLength));
					const rangeEnd = (parcel + 1) * Math.pow(10, Math.max(0, 4 - parcelLength)) - 1;
					where = `${gushFieldName} = ${gush} AND ${parcelFieldName} >= ${rangeStart} AND ${parcelFieldName} <= ${rangeEnd}`;
				}
			}
		}
		
		return layer.queryFeatures({ 
			where, 
			outFields: ['*'], 
			returnGeometry: true,
			orderByFields: [`${parcelFieldName} ASC`]
		})
			.then((result: any) => {
				return result.features.map((feature: any, index: number) => {
					const gushValue = feature.attributes[gushFieldName];
					const parcelValue = feature.attributes[parcelFieldName];
					const text = `${this.translate.instant('iaa.map.gush')}: ${gushValue}, ${this.translate.instant('iaa.map.parcel')}: ${parcelValue}`;
					return {
						key: `${gushValue}-${parcelValue}`,
						text: text,
						sourceIndex: params.sourceIndex,
						feature: feature
					};
				});
			})
			.catch(() => []);
	}

	getResults(params: any, layer: any, gushFieldName: string, parcelFieldName: string): Promise<any[]> {
		// When user selects from suggestions, use that exact feature
		if (params.suggestResult?.feature) {
			return Promise.resolve([{
				extent: params.suggestResult.feature.geometry.extent,
				feature: params.suggestResult.feature,
				name: params.suggestResult.text
			}]);
		}
		
		// When user presses Enter without selecting a suggestion, parse the search term
		let searchTerm = params.searchTerm || params.suggestTerm || params.term || '';
		
		// If we have suggestResult.text but no feature, it means Enter was pressed and first suggestion was auto-selected
		// Extract numbers from the formatted text like "גוש: 3692, חלקה: 1"
		if (!searchTerm && params.suggestResult?.text) {
			const numbers = params.suggestResult.text.match(/\d+/g);
			if (numbers && numbers.length >= 2) {
				searchTerm = `${numbers[0]}/${numbers[1]}`;
			}
		}
		
		if (!searchTerm) {
			return Promise.resolve([]);
		}
		
		const parts = searchTerm.split(/[\s\/\-,]+/).filter((p: string) => p.trim() && !isNaN(Number(p)));
		
		if (parts.length !== 2) {
			return Promise.resolve([]);
		}
		
		const [gushStr, parcelStr] = parts;
		const gush = parseInt(gushStr, 10);
		const parcel = parseInt(parcelStr, 10);
		
		if (isNaN(gush) || isNaN(parcel)) {
			return Promise.resolve([]);
		}
		
		const where = `${gushFieldName} = ${gush} AND ${parcelFieldName} = ${parcel}`;
		
		return layer.queryFeatures({ where, outFields: ['*'], returnGeometry: true })
			.then((result: any) => {
				if (!result.features || result.features.length === 0) {
					return [];
				}
				// Return only the first matching feature
				const feature = result.features[0];
				return [{
					extent: feature.geometry.extent,
					feature: feature,
					name: `${this.translate.instant('iaa.map.gush')}: ${feature.attributes[gushFieldName]}, ${this.translate.instant('iaa.map.parcel')}: ${feature.attributes[parcelFieldName]}`
				}];
			})
			.catch(() => []);
	}
}
